import { Box, Checkbox, Collapse, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { createSlot } from "lib/@components/slot/create-slot"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import Iconify from "minimals-template/components/Iconify"
import { useMemo, useState } from "react"
import { buildScheduleContent } from "authoring/routes/macro/lib/macro-conversion"
import { TaskContent } from "../overwrite/task-correlator/components/task-content"
import { Classification } from "routes/schedule/tasks/classification"
import { Frequency } from "routes/schedule/tasks/frequency"
import { SkillSet } from "routes/schedule/tasks/skill-set"
import { Time } from "routes/schedule/tasks/time"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export const TaskRowUpperButton = createSlot("TaskRowUpperButton")
export const TaskRowLowerButton = createSlot("TaskRowLowerButton")

export function AddGroupTaskRow({ task, index }) {
    const { group, macroMap } = useBoundContext()
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const [showMore, setShowMore] = useState(false)
    const displayTask = useMemo(expandTask, [task, macroMap])
    const { classification, title, minutes } = displayTask
    const selectionCheckbox = canEditSchedule ? <Checkbox disabled={group.readOnly} /> : null
    const rowActions = canEditSchedule ? <TaskRowUpperButton.Slot /> : null

    return (
        <Bound task={displayTask} target={displayTask} originalTask={task} index={index}>
            <TableRow onClick={toggleShowMore} sx={{ cursor: "pointer" }} hover data-cy="add-group-task-row">
                <TableCell sx={{ width: 70, maxWidth: 70, whiteSpace: "noWrap", py: 0 }}>{selectionCheckbox}</TableCell>
                <TableCell sx={{ maxWidth: { xs: 150, md: 300 }, whiteSpace: "noWrap", py: 0, pl: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Classification classification={classification} />
                        <TruncatedTypography
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                flexGrow: 1,
                            }}
                        >
                            {title}
                        </TruncatedTypography>
                    </Stack>
                </TableCell>
                <TableCell sx={{ width: 100, maxWidth: 100, whiteSpace: "noWrap", py: 0 }}>
                    {minutes ? <Time /> : <Typography>-</Typography>}
                </TableCell>
                <TableCell sx={{ width: 130, maxWidth: 130, whiteSpace: "noWrap", py: 0 }}>
                    <Frequency />
                </TableCell>
                <TableCell sx={{ width: 80, maxWidth: 80, whiteSpace: "noWrap", py: 1 }}>
                    <SkillSet sx={{ marginLeft: 0 }} />
                </TableCell>
                <TableCell sx={{ width: 60, maxWidth: 60, whiteSpace: "noWrap", py: 0 }}>{rowActions}</TableCell>
                <TableCell
                    sx={{
                        width: 44,
                        maxWidth: 44,
                        py: 0,
                    }}
                >
                    <IconButton
                        aria-label={showMore ? "Hide task details" : "Show task details"}
                        onClick={handleExpandButtonClick}
                        data-cy="expand-task-content"
                    >
                        <Iconify icon={showMore ? "mingcute:up-fill" : "mingcute:down-fill"} />
                    </IconButton>
                </TableCell>

                <TableCell sx={{ width: 60, maxWidth: 60, whiteSpace: "noWrap", py: 0 }} align="center">
                    <TaskRowLowerButton.Slot />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={8} sx={{ p: 0 }}>
                    <Box sx={{ overflow: "hidden", maxWidth: 1 }}>
                        <Collapse in={showMore} mountOnEnter sx={{ width: 1 }}>
                            <TaskContent />
                        </Collapse>
                    </Box>
                </TableCell>
            </TableRow>
        </Bound>
    )

    function toggleShowMore() {
        setShowMore((show) => !show)
    }

    function handleExpandButtonClick(event) {
        event.stopPropagation()
        toggleShowMore()
    }

    function expandTask() {
        if (!macroMap || !Object.keys(macroMap).length) return task
        return buildScheduleContent({ tasks: [task] }, macroMap)?.tasks?.[0] ?? task
    }
}
